import { useAuth0 } from '@auth0/auth0-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  getLexOfficeRuleset,
  upsertLexOfficeRuleset,
  deleteLexOfficeRuleset,
  improveCustomRule,
} from '../apis/lexOfficeRulesetApi';
import { ImproveCustomRuleRequest, UpsertLexOfficeRulesetRequest } from '../apis/types';

export const useLexOfficeRuleset = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  const {
    data: ruleset = null,
    isLoading: loading,
    error: queryError,
  } = useQuery({
    queryKey: ['lexOfficeRuleset'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return getLexOfficeRuleset(token);
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const upsertMutation = useMutation({
    mutationFn: async (data: UpsertLexOfficeRulesetRequest) => {
      const token = await getAccessTokenSilently();
      return upsertLexOfficeRuleset(data, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lexOfficeRuleset'] });
    },
    onError: error => {
      console.error('Error upserting ruleset:', error);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const token = await getAccessTokenSilently();
      return deleteLexOfficeRuleset(token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lexOfficeRuleset'] });
    },
    onError: error => {
      console.error('Error deleting ruleset:', error);
    },
  });

  const improveMutation = useMutation({
    mutationFn: async (data: ImproveCustomRuleRequest) => {
      const token = await getAccessTokenSilently();
      return improveCustomRule(data, token);
    },
  });

  const error =
    queryError?.message ||
    upsertMutation.error?.message ||
    deleteMutation.error?.message ||
    null;

  return {
    ruleset,
    loading,
    saving: upsertMutation.isPending,
    deleting: deleteMutation.isPending,
    improving: improveMutation.isPending,
    error,
    upsertRuleset: upsertMutation.mutateAsync,
    deleteRuleset: deleteMutation.mutateAsync,
    improveCustomRule: improveMutation.mutateAsync,
  };
};
